import { useState, type FormEvent } from 'react';
import { Lock } from '@phosphor-icons/react';
import { mapAuthError } from '@mybusinessmate/api-client';
import { changePasswordInputSchema } from '@mybusinessmate/schema';
import { authClient } from '../../lib/api';
import { passwordStrength } from '../../lib/password-strength';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { PasswordInput } from '../ui/input';
import { PasswordStrength } from '../ui/password-strength';
import { useToast } from '../ui/toast';

type Fields = { currentPassword: string; newPassword: string; confirmPassword: string };
const EMPTY: Fields = { currentPassword: '', newPassword: '', confirmPassword: '' };

function Body({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [values, setValues] = useState<Fields>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({});
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const set = (key: keyof Fields) => (e: { target: { value: string } }) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(undefined);
    const parsed = changePasswordInputSchema.safeParse(values);
    if (!parsed.success) {
      const next: Partial<Record<keyof Fields, string>> = {};
      for (const issue of parsed.error.issues) next[issue.path[0] as keyof Fields] ??= issue.message;
      setErrors(next);
      return;
    }
    setErrors({});
    setBusy(true);
    const { error } = await authClient.changePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
      revokeOtherSessions: true,
    });
    setBusy(false);
    const mapped = mapAuthError(error);
    if (mapped) {
      if (mapped.code === 'INVALID_PASSWORD') setErrors({ currentPassword: 'That isn’t your current password.' });
      else setFormError(mapped.message);
      return;
    }
    toast({ title: 'Password changed', description: 'You’ve been signed out of your other devices.' });
    onClose();
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-(--mbm-space-5)">
      {formError ? <Banner tone="error">{formError}</Banner> : null}
      <PasswordInput
        label="Current password"
        autoComplete="current-password"
        autoFocus
        value={values.currentPassword}
        error={errors.currentPassword}
        onChange={set('currentPassword')}
      />
      <div className="flex flex-col gap-(--mbm-space-3)">
        <PasswordInput
          label="New password"
          autoComplete="new-password"
          value={values.newPassword}
          error={errors.newPassword}
          onChange={set('newPassword')}
        />
        <PasswordStrength level={passwordStrength(values.newPassword)} />
      </div>
      <PasswordInput
        label="Confirm new password"
        autoComplete="new-password"
        value={values.confirmPassword}
        error={errors.confirmPassword}
        onChange={set('confirmPassword')}
      />
      <div className="flex flex-col-reverse gap-(--mbm-space-2) sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" loading={busy}>
          Change password
        </Button>
      </div>
    </form>
  );
}

export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      icon={Lock}
      title="Change password"
      description="At least 8 characters, with a number and a symbol. Other devices will be signed out."
    >
      <Body onClose={onClose} />
    </Dialog>
  );
}
